import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import { useStore } from 'zustand';
import appUiStore from '../../../stores/AppUiStore';
import { Lesson } from '../../../types/lesson/LessonType';
import { ItemRowMenuItemType } from '../../../types/ui/ItemRowMenuItem';
import { SNACKBAR_SEVERITY } from '../../../types/ui/VizzleSnackbar';
import WindowParentUtil from '../../../utils/WindowParentUtil';
import Logger from '../../../utils/Logger';

/**
 * Public paths, served by the parent (host) application, that open a detail plan page.
 * - lesson: keyed by uuid so the link survives re-versioning
 * - unit: keyed by unit id (units have no uuid)
 * - topic: keyed by topic id, plus the instruction level being viewed
 */
export const DETAIL_PLAN_LINK_PATH = {
  lesson: 'lesson-plan',
  unit: 'unit-plan',
  topic: 'topic-plan',
} as const;

/** Query parameter carrying the instruction level on a topic detail plan link */
export const DETAIL_PLAN_LINK_LEVEL_PARAM = 'level';

type DetailPlanLinkKind = keyof typeof DETAIL_PLAN_LINK_PATH;

export const buildDetailPlanLink = (kind: DetailPlanLinkKind, id: string, query?: Record<string, string>) => {
  const search = query ? new URLSearchParams(query).toString() : '';
  return `${WindowParentUtil.getParentUrl()}${DETAIL_PLAN_LINK_PATH[kind]}/${id}${search ? `?${search}` : ''}`;
};

const copyTextToClipboard = async (text: string) => {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (e) {
    // Fall through to the legacy approach (e.g. clipboard blocked inside the iframe)
    Logger.logError(e);
  }

  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);
  textarea.select();
  let copied = false;
  try {
    copied = document.execCommand('copy');
  } catch (e) {
    Logger.logError(e);
  } finally {
    document.body.removeChild(textarea);
  }
  return copied;
};

export const useDetailPlanLinkAction = () => {
  const openSnackbar = useStore(appUiStore, (state) => state.openSnackbar);

  const getDetailPlanLinkAction = (
    kind: DetailPlanLinkKind,
    id: string,
    query?: Record<string, string>,
  ): ItemRowMenuItemType => {
    return {
      id: `detail-plan-link-action-${kind}`,
      label: 'Detail Plan Link',
      icon: <DescriptionOutlinedIcon />,
      onClick: async (closeDropdownFunction) => {
        closeDropdownFunction();
        const copied = await copyTextToClipboard(buildDetailPlanLink(kind, id, query));
        openSnackbar({
          severity: copied ? SNACKBAR_SEVERITY.INFO : SNACKBAR_SEVERITY.ERROR,
          open: true,
          message: copied
            ? 'The detail plan link has been copied to the clipboard'
            : 'The detail plan link could not be copied. Please try again.',
          duration: copied ? 3000 : 10000,
        });
      },
    };
  };

  const getLessonDetailPlanLinkAction = (lesson: Lesson) => getDetailPlanLinkAction('lesson', lesson.attributes.uuid);

  const getUnitDetailPlanLinkAction = (unitId: string) => getDetailPlanLinkAction('unit', unitId);

  /**
   * Topic link. The level is only included when one is being viewed, so the
   * recipient lands on the same leveled content the sharer was planning from.
   */
  const getTopicDetailPlanLinkAction = (topicId: string, level?: string) =>
    getDetailPlanLinkAction('topic', topicId, level ? { [DETAIL_PLAN_LINK_LEVEL_PARAM]: level } : undefined);

  return { getLessonDetailPlanLinkAction, getUnitDetailPlanLinkAction, getTopicDetailPlanLinkAction };
};
